import type { CashoutQuote, MyBet, MyBetsResponse } from '@ps/isp-web-services-types';
import type { models } from '@ps/web-core-store';

import type { LiveOption } from '@swf/sports/types/liveOption';

import type { BetsRequestedPayLoad } from './effects';
import type { SportsMyBetsState } from './types';

type LocationUpdatePayload = Parameters<typeof models.location.reducers.update>[1];

interface BetId {
  betId: string;
}

interface BetsLoadedSuccessfullyPayload extends MyBetsResponse {
  isSettled?: boolean;
  fromRecord?: number;
  isLoadingMoreBets?: boolean;
}

interface CashedOutSuccessfullyPayload extends BetId {
  cashedOutAmount: number;
}

interface CashOutConfirmationPayload extends BetId {
  isAwaitingCashOutConfirmation: boolean;
}

interface CashOutFailurePayload extends BetId {
  cashOutError: string;
}

type CashOutClearErrorPayload = BetId;
type CashOutSubmitPayload = BetId;

export const reducers = {
  betsLoadedSuccessfully: (
    state: SportsMyBetsState,
    payload: BetsLoadedSuccessfullyPayload
  ): SportsMyBetsState => {
    if (state.isSettled !== payload.isSettled) {
      return state;
    }
    const bets = payload.fromRecord === 1 ? [...payload.bets] : [...state.bets, ...payload.bets];

    const filteredBets = bets.filter((bet) => bet.isSettled === Boolean(state.isSettled));

    const uniqueBetIds = [...new Set(filteredBets.map((bet) => bet.betId))];

    const uniqueBets = uniqueBetIds.map((id) =>
      filteredBets.find((bet) => bet.betId === id)
    ) as MyBet[];

    return {
      ...state,
      showLoader: false,
      isLoadingMoreBets: false,
      bets: uniqueBets,
      cashOutQuotes: { ...state.cashOutQuotes, ...payload.cashoutQuotes },
      moreAvailable: payload.moreAvailable,
      numberOfMatches: payload.numberOfMatches,
      /*
       * If we are loading more and the new events don't either have an in-play event (lower pollingInterval),
       * or it is the same we don't want to update the polling interval state.
       */
      pollingInterval:
        payload.isLoadingMoreBets && payload.pollingInterval >= state.pollingInterval
          ? state.pollingInterval
          : payload.pollingInterval
    };
  },
  betsLoadedUnsuccessfully: (state: SportsMyBetsState): SportsMyBetsState => ({
    ...state,
    showLoader: false,
    isErroneous: true,
    isLoadingMoreBets: false
  }),
  betsRequested: (
    state: SportsMyBetsState,
    { showLoader, isSettled, isLoadingMoreBets }: BetsRequestedPayLoad
  ): SportsMyBetsState => ({
    ...state,
    showLoader: Boolean(showLoader),
    isLoadingMoreBets: Boolean(isLoadingMoreBets),
    isErroneous: false,
    isSettled
  }),
  betsChanged: (state: SportsMyBetsState, payload: LocationUpdatePayload): SportsMyBetsState => {
    const pathname = typeof payload === 'string' ? payload : payload.pathname;
    const hasMyBetsChanged =
      (pathname.includes('/my-bets/open') && state.isSettled) ||
      (pathname.includes('/my-bets/settled') && !state.isSettled);

    if (hasMyBetsChanged) {
      return {
        ...state,
        showLoader: true,
        isErroneous: false
      };
    }

    return state;
  },
  cashedOutSuccessfully: (
    state: SportsMyBetsState,
    payload: CashedOutSuccessfullyPayload
  ): SportsMyBetsState => ({
    ...state,
    cashedOut: { ...state.cashedOut, [payload.betId]: payload.cashedOutAmount }
  }),
  cashOutQuotesUpdated: (
    state: SportsMyBetsState,
    payload: Record<string, CashoutQuote>
  ): SportsMyBetsState => ({
    ...state,
    cashOutQuotes: payload
  }),
  cashOutSubmit: (
    state: SportsMyBetsState,
    { betId }: CashOutSubmitPayload
  ): SportsMyBetsState => ({
    ...state,
    cashOutBets: {
      ...state.cashOutBets,
      [betId]: {
        cashOutError: undefined,
        isAwaitingCashOutConfirmation: true,
        isSubmittingCashOut: true
      }
    }
  }),
  cashOutFailure: (
    state: SportsMyBetsState,
    { cashOutError, betId }: CashOutFailurePayload
  ): SportsMyBetsState => ({
    ...state,
    cashOutBets: {
      ...state.cashOutBets,
      [betId]: {
        cashOutError,
        isAwaitingCashOutConfirmation: false,
        isSubmittingCashOut: false
      }
    }
  }),
  cashOutConfirmation: (
    state: SportsMyBetsState,
    { isAwaitingCashOutConfirmation, betId }: CashOutConfirmationPayload
  ): SportsMyBetsState => ({
    ...state,
    cashOutBets: {
      ...state.cashOutBets,
      [betId]: {
        cashOutError: undefined,
        isSubmittingCashOut: false,
        isAwaitingCashOutConfirmation
      }
    }
  }),
  cashOutClearError: (
    state: SportsMyBetsState,
    { betId }: CashOutClearErrorPayload
  ): SportsMyBetsState => ({
    ...state,
    cashOutBets: {
      ...state.cashOutBets,
      [betId]: {
        cashOutError: undefined,
        isAwaitingCashOutConfirmation: false,
        isSubmittingCashOut: false
      }
    }
  }),
  setSelectedLiveOption: (
    state: SportsMyBetsState,
    selectedLiveOption: { id?: string; accordionIds?: string[]; liveOption?: LiveOption }
  ): SportsMyBetsState => ({
    ...state,
    selectedLiveOption
  })
};
